import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { FaArrowLeft, FaCalendarDay, FaDownload } from "react-icons/fa";
import { descargarCsv } from "./csv";
import Spinner from "./Spinner";

const fmt = (v) =>
    v === null || v === undefined ? "—" : Number(v).toLocaleString("es-MX");

export default function CpsDetalleCuenta() {
    const navigate = useNavigate();
    const { iAccount } = useParams();
    const [sp] = useSearchParams();

    const [meses, setMeses] = useState(
        (sp.get("meses") || "").split(",").filter(Boolean)
    );
    const remoteIp = sp.get("ip") || null;
    const [username, setUsername] = useState(null);
    const [dias, setDias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sort, setSort] = useState({ col: "fecha", dir: "asc" });

    const toggleSort = (col) =>
        setSort((s) =>
            s.col === col
                ? { col, dir: s.dir === "asc" ? "desc" : "asc" }
                : { col, dir: "desc" }
        );

    // Si no llegaron meses por la URL, usa todos los disponibles.
    useEffect(() => {
        if (meses.length > 0) return;
        axios
            .post(API_ROUTES.analisis.meses, {})
            .then((res) => setMeses(res.data?.meses || []))
            .catch(() => setMeses([]));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (meses.length === 0) return;
        let cancel = false;
        setLoading(true);
        setError(null);
        axios
            .post(API_ROUTES.analisis.dias, {
                i_account: Number(iAccount),
                meses,
                remote_ip: remoteIp,
            })
            .then((res) => {
                if (cancel) return;
                setUsername(res.data?.username ?? null);
                setDias(Array.isArray(res.data?.dias) ? res.data.dias : []);
            })
            .catch((err) => {
                if (cancel) return;
                setError(err?.response?.data?.message || err?.message || "Error");
                setDias([]);
            })
            .finally(() => !cancel && setLoading(false));
        return () => {
            cancel = true;
        };
    }, [iAccount, meses, remoteIp]);

    // Resumen del periodo (pico = MAX, prom/dia = AVG), para contexto.
    const resumen = useMemo(() => {
        if (dias.length === 0) return null;
        const n = dias.length;
        const cpsPico = Math.max(...dias.map((d) => Number(d.cps_pico || 0)));
        const simPico = Math.max(...dias.map((d) => Number(d.simultaneas_pico || 0)));
        const cpsProm =
            dias.reduce((a, d) => a + Number(d.cps_pico || 0), 0) / n;
        const simProm =
            dias.reduce((a, d) => a + Number(d.simultaneas_pico || 0), 0) / n;
        const llamadas = dias.reduce((a, d) => a + Number(d.total_llamadas || 0), 0);
        const contest = dias.reduce(
            (a, d) => a + Number(d.llamadas_contestadas || 0),
            0
        );
        return { n, cpsPico, simPico, cpsProm, simProm, llamadas, contest };
    }, [dias]);

    // Ordenamiento por columna (asc/desc). El día ordena por fecha; el resto numerico.
    const diasOrdenados = useMemo(() => {
        const arr = [...dias];
        const { col, dir } = sort;
        const factor = dir === "asc" ? 1 : -1;
        arr.sort((a, b) => {
            if (col === "fecha") {
                return String(a.fecha).localeCompare(String(b.fecha)) * factor;
            }
            return (Number(a[col] || 0) - Number(b[col] || 0)) * factor;
        });
        return arr;
    }, [dias, sort]);

    const exportar = () =>
        descargarCsv(
            [
                { key: "fecha", label: "dia", format: (v) => String(v).slice(0, 10) },
                { key: "cps_pico", label: "cps_pico" },
                { key: "simultaneas_pico", label: "simultaneas_pico" },
                { key: "total_llamadas", label: "llamadas" },
                { key: "llamadas_contestadas", label: "contestadas" },
                { key: "no_contestadas", label: "no_contestadas" },
            ],
            diasOrdenados,
            `cps_dias_cuenta${iAccount}${remoteIp ? "_ip" + remoteIp.replace(/[.:]/g, "-") : ""}_${meses.join("_") || "periodo"}`
        );

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            <button
                onClick={() => navigate("/analisis/cps")}
                className="text-sm text-slate-500 hover:text-cyan-700 flex items-center gap-2 mb-4"
            >
                <FaArrowLeft /> Volver al resumen
            </button>

            <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <FaCalendarDay className="text-cyan-600" />
                        {username || `Cuenta ${iAccount}`}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {remoteIp ? (
                            <>
                                IP <span className="font-mono text-slate-700">{remoteIp}</span> ·
                                detalle por día. Haz clic en un día para ver sus horas.
                            </>
                        ) : (
                            <>Detalle por día. Haz clic en un día para ver sus horas.</>
                        )}
                    </p>
                </div>
                {dias.length > 0 && !loading && (
                    <button
                        onClick={exportar}
                        className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-700"
                    >
                        <FaDownload /> Descargar CSV
                    </button>
                )}
            </div>

            {resumen && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                    <Kpi label="CPS pico" value={resumen.cpsPico} />
                    <Kpi label="CPS prom/día" value={resumen.cpsProm.toFixed(1)} />
                    <Kpi label="Simult. pico" value={resumen.simPico} />
                    <Kpi label="Simult. prom/día" value={resumen.simProm.toFixed(1)} />
                </div>
            )}

            {error && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-700 text-sm">
                    {error}
                </div>
            )}

            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 text-slate-500 text-left">
                            <SortTh col="fecha" sort={sort} onClick={toggleSort}>Día</SortTh>
                            <SortTh col="cps_pico" sort={sort} onClick={toggleSort} right>CPS pico</SortTh>
                            <SortTh col="simultaneas_pico" sort={sort} onClick={toggleSort} right>Simult. pico</SortTh>
                            <SortTh col="total_llamadas" sort={sort} onClick={toggleSort} right>Llamadas</SortTh>
                            <SortTh col="llamadas_contestadas" sort={sort} onClick={toggleSort} right>Contestadas</SortTh>
                            <SortTh col="no_contestadas" sort={sort} onClick={toggleSort} right>No contestadas</SortTh>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={6}>
                                    <Spinner label="Cargando días…" />
                                </td>
                            </tr>
                        ) : dias.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="text-center py-10 text-slate-400">
                                    Sin días para el periodo seleccionado.
                                </td>
                            </tr>
                        ) : (
                            diasOrdenados.map((d) => {
                                const fecha = String(d.fecha).slice(0, 10);
                                return (
                                    <tr
                                        key={fecha}
                                        onClick={() =>
                                            navigate(
                                                `/analisis/cps/${iAccount}/${fecha}` +
                                                (remoteIp
                                                    ? `?ip=${encodeURIComponent(remoteIp)}`
                                                    : ""),
                                                { state: { username, remoteIp } }
                                            )
                                        }
                                        className="border-t border-slate-100 hover:bg-cyan-50/50 cursor-pointer"
                                        title="Ver detalle por hora"
                                    >
                                        <Td>{fecha}</Td>
                                        <Td right>{fmt(d.cps_pico)}</Td>
                                        <Td right>{fmt(d.simultaneas_pico)}</Td>
                                        <Td right>{fmt(d.total_llamadas)}</Td>
                                        <Td right>{fmt(d.llamadas_contestadas)}</Td>
                                        <Td right>{fmt(d.no_contestadas)}</Td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Kpi({ label, value }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-xs text-slate-500">{label}</div>
            <div className="text-2xl font-bold text-slate-800">{fmt(value)}</div>
        </div>
    );
}
function Th({ children, right }) {
    return (
        <th className={`px-3 py-2 font-medium ${right ? "text-right" : ""}`}>
            {children}
        </th>
    );
}
function SortTh({ col, sort, onClick, right, children }) {
    const active = sort.col === col;
    const arrow = !active ? "↕" : sort.dir === "asc" ? "↑" : "↓";
    return (
        <th
            onClick={() => onClick(col)}
            className={`px-3 py-2 font-medium cursor-pointer select-none hover:text-cyan-700 ${right ? "text-right" : ""
                }`}
        >
            <span className="inline-flex items-center gap-1">
                {right && <span className={active ? "text-cyan-600" : "text-slate-300"}>{arrow}</span>}
                {children}
                {!right && <span className={active ? "text-cyan-600" : "text-slate-300"}>{arrow}</span>}
            </span>
        </th>
    );
}
function Td({ children, right }) {
    return (
        <td className={`px-3 py-2 ${right ? "text-right tabular-nums" : ""}`}>
            {children}
        </td>
    );
}